import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { useAppUpgradeStore } from '@/stores/appUpgradeStore'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useAppInfo } from '@/composables/useAppInfo'
import { isVersionLessThan } from '@/utils/versionCompare'

/**
 * Evaluate API mobile_app_version payload and update upgrade store state.
 */
export function evaluateMobileAppVersion(mobileAppVersion) {
  if (!import.meta.env.VITE_MOBILE_APP || !mobileAppVersion) return

  const { getAppVersion, getNativePlatform } = useAppInfo()
  const appUpgradeStore = useAppUpgradeStore()
  const currentVersion = getAppVersion()
  const platform = getNativePlatform()

  const platformConfig = mobileAppVersion[platform] || mobileAppVersion
  const {
    update_required,
    force_update,
    minimum_version,
    latest_version,
    message
  } = platformConfig

  if (force_update) {
    appUpgradeStore.setUpgradeInfo({
      update_required: true,
      force_update: true,
      minimum_version,
      latest_version,
      message
    })
    return
  }

  const needsSoftUpdate =
    update_required &&
    (!latest_version || isVersionLessThan(currentVersion, latest_version))

  if (needsSoftUpdate) {
    appUpgradeStore.setUpgradeInfo({
      update_required: true,
      force_update: false,
      minimum_version,
      latest_version,
      message
    })
  } else {
    appUpgradeStore.reset()
  }
}

/**
 * Handle upgrade info from API error response body or headers.
 */
export function handleUpgradeApiResponse(response) {
  if (!import.meta.env.VITE_MOBILE_APP || !response) return

  const data = response.data
  if (data?.mobile_app_version) {
    evaluateMobileAppVersion(data.mobile_app_version)
    return
  }
  if (data?.force_update !== undefined || data?.update_required !== undefined) {
    evaluateMobileAppVersion(data)
  }
}

export function useAppUpgrade() {
  const appUpgradeStore = useAppUpgradeStore()
  const restoStore = useRestoGLobalStore()
  const { getNativePlatform } = useAppInfo()

  const getStoreUrl = () => {
    const platform = getNativePlatform()
    const ids = restoStore?.restoInfo?.mobileapp_ids
    if (platform === 'ios' && ids?.itunes_app_id) {
      return `https://apps.apple.com/app/${ids.itunes_app_id}`
    }
    if (platform === 'android' && ids?.playstore_app_id) {
      return `https://play.google.com/store/apps/details?id=${ids.playstore_app_id}`
    }
    return null
  }

  const openAppStore = async () => {
    const storeUrl = getStoreUrl()
    if (!storeUrl) {
      console.warn('No app store URL configured for this restaurant')
      return
    }
    if (Capacitor.isNativePlatform()) {
      await App.openUrl({ url: storeUrl })
    } else {
      window.open(storeUrl, '_blank')
    }
  }

  return {
    appUpgradeStore,
    openAppStore,
    getStoreUrl
  }
}
